import {
  Component, AfterViewInit, OnDestroy, ViewChild, ElementRef, NgZone
} from '@angular/core';
import * as THREE from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader';
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry';

@Component({
  selector: 'app-glass-cube',
  templateUrl: './glass-cube.component.html',
  styleUrls: ['./glass-cube.component.scss']
})
export class GlassCubeComponent implements AfterViewInit, OnDestroy {

  @ViewChild('canvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private renderer!: THREE.WebGLRenderer;
  private scene!: THREE.Scene;
  private camera!: THREE.PerspectiveCamera;
  private animationId!: number;
  private clock = new THREE.Clock();
  private group!: THREE.Group;
  private visibilityObserver!: IntersectionObserver;
  private isVisible = true;
  private isHovered = false;

  constructor(private ngZone: NgZone) {}

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => this.init());
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animationId);
    this.renderer?.dispose();
    this.visibilityObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
    const canvas = this.canvasRef?.nativeElement;
    canvas?.removeEventListener('mouseenter', this.onHoverStart);
    canvas?.removeEventListener('mouseleave', this.onHoverEnd);
  }

  private init(): void {
    const canvas = this.canvasRef.nativeElement;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;

    this.scene = new THREE.Scene();

    this.camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    this.camera.position.set(0, 0, 7);

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setSize(w, h);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;
    (this.renderer as any).physicallyCorrectLights = true;

    // ─── Luzes ────────────────────────────────────────
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.3));

    // Luz de topo para reflexo no vidro
    const topLight = new THREE.DirectionalLight(0xffffff, 3.0);
    topLight.position.set(2, 8, 4);
    this.scene.add(topLight);

    // Rim light de trás (cria o brilho nas bordas)
    const rimLight = new THREE.DirectionalLight(0xaaccff, 3.5);
    rimLight.position.set(-4, 2, -8);
    this.scene.add(rimLight);

    // Luz frontal suave
    const frontLight = new THREE.DirectionalLight(0xffffff, 1.0);
    frontLight.position.set(1, 3, 10);
    this.scene.add(frontLight);

    this.group = new THREE.Group();
    this.group.scale.setScalar(1.2);
    this.scene.add(this.group);

    // ─── Luz roxa interna (glow no interior) ─────────
    const purpleGlow = new THREE.PointLight(0x7c3aed, 4.0, 4.0);
    purpleGlow.position.set(0, 0, 0);
    this.group.add(purpleGlow);

    // ─── Interior com gradiente roxo ──────────────────
    const bgMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(0x1e0048),
      emissive: new THREE.Color(0x000000),
      emissiveIntensity: 1.0,
      roughness: 1.0,
      metalness: 0.0,
    });
    const bgMesh = new THREE.Mesh(new THREE.BoxGeometry(1.88, 1.88, 0.55), bgMat);
    bgMesh.renderOrder = 0;
    this.group.add(bgMesh);

    // ─── Logo ────────────────────────────────────────
    const texLoader = new THREE.TextureLoader();
    const photoMat = new THREE.MeshBasicMaterial({
      transparent: true,
      alphaTest: 0.05,
      side: THREE.FrontSide,
    });
    const photoMesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), photoMat);
    photoMesh.position.z = 0.32;
    photoMesh.renderOrder = 1;
    this.group.add(photoMesh);

    texLoader.load('assets/img/logo.png', (tex) => {
      (tex as any).encoding = 3001;
      photoMat.map = tex;
      photoMat.needsUpdate = true;
      if (tex.image) {
        const aspect = tex.image.width / tex.image.height;
        const s = 0.9;
        photoMesh.scale.set(s * aspect, s, 1);
      }
      if (!this.isHovered) this.renderer.render(this.scene, this.camera);
    });

    // ─── Vidro ─ tintado escuro com bordas chrome ─────
    const glassMat = new THREE.MeshPhysicalMaterial({
      color: new THREE.Color(0x223355),
      transmission: 0.72,
      opacity: 1.0,
      metalness: 0.0,
      roughness: 0.02,
      ior: 1.72,
      thickness: 1.1,
      attenuationColor: new THREE.Color(0x334466),
      attenuationDistance: 1.2,
      specularIntensity: 1.0,
      envMapIntensity: 2.5,
      clearcoat: 1.0,
      clearcoatRoughness: 0.02,
      transparent: true,
      side: THREE.FrontSide,
      depthWrite: false,
    } as any);

    const glassMesh = new THREE.Mesh(
      new (RoundedBoxGeometry as any)(2.1, 2.1, 0.95, 32, 0.26),
      glassMat
    );
    glassMesh.renderOrder = 2;
    this.group.add(glassMesh);

    // ─── HDRI (reflexos realistas nas bordas) ────────
    const pmrem = new THREE.PMREMGenerator(this.renderer);
    pmrem.compileEquirectangularShader();
    new RGBELoader().load(
      'assets/img/royal_esplanade_1k.hdr',
      (hdr) => {
        const envMap = pmrem.fromEquirectangular(hdr).texture;
        this.scene.environment = envMap;
        hdr.dispose();
        pmrem.dispose();
        this.canvasRef.nativeElement.classList.add('ready');
        if (!this.isHovered) this.renderer.render(this.scene, this.camera);
      },
      undefined,
      () => pmrem.dispose()
    );

    window.addEventListener('resize', this.onResize);

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        this.isVisible = entries[0].isIntersecting;
        if (this.isVisible && this.isHovered) this.animate();
      },
      { threshold: 0.1 }
    );
    this.visibilityObserver.observe(canvas);

    // Frame estático inicial; anima apenas no hover
    this.renderer.render(this.scene, this.camera);
    canvas.addEventListener('mouseenter', this.onHoverStart);
    canvas.addEventListener('mouseleave', this.onHoverEnd);
  }

  private onHoverStart = (): void => {
    this.isHovered = true;
    this.animate();
  }

  private onHoverEnd = (): void => {
    this.isHovered = false;
    cancelAnimationFrame(this.animationId);
  }

  private animate = (): void => {
    if (!this.isVisible) return;
    if (!this.isHovered) return;

    this.animationId = requestAnimationFrame(this.animate);
    const t = this.clock.getElapsedTime();

    this.group.rotation.y = Math.sin(t * 0.4) * 0.55;
    this.group.rotation.x = Math.cos(t * 0.3) * 0.18;
    this.group.rotation.z = Math.sin(t * 0.2) * 0.09;

    this.renderer.render(this.scene, this.camera);
  }

  private onResize = (): void => {
    const canvas = this.canvasRef.nativeElement;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h);
  }
}
